/* eslint-disable no-unused-vars */
import React, { useState, useRef, useContext, useMemo } from 'react';
import { useHistory } from 'react-router';
import { IonButton, isPlatform } from '@ionic/react';
import { PageContext } from '../../../contexts/AppContexts';
import { AppContext, AppDispatchContext, ACTIONS } from '../../../global/AppContext';
import { rejectFight, TAKEOUT_TYPES } from '../../../global/request/bet';
import { AuthContext } from '../../authentication/AuthContext';
import { StartFightContextProvider } from '../StartFightContext';
import TakerError from './TakerError';
import TakerFightProcess from './TakerFightProcess';
import TakerGamePanel from './TakerGamePanel';

import '../../../styles/fight/takerProcess/TakerNotification.scss';

const TakerNotification = ({ isOpen, onClose, fightData }) => {
  if (!isOpen || !fightData) return null;
  const authCtx = useContext(AuthContext);
  const appCtx = useContext(AppContext);
  const appDispatchCtx = useContext(AppDispatchContext);
  const pageCtx = useContext(PageContext);
  const bearerToken = authCtx.tokens.idToken;
  const [closing, setClosing] = useState(false);
  const fightModalRef = useRef();

  const [isPickVenuePopupOpen, setIsPickVenuePopupOpen] = useState(false);
  const [onSelectVenue, setOnSelectVenue] = useState(null);
  const history = useHistory();
  const optionalTakerVenue = useMemo(() => {
    if (fightData.takeoutType === TAKEOUT_TYPES.DELIVERY && appCtx.restaurant) {
      return {
        id: appCtx.restaurant.id,
        name: appCtx.restaurant.name,
        takeoutType: appCtx.isDelivery ? TAKEOUT_TYPES.DELIVERY : TAKEOUT_TYPES.IN_PERSON,
      };
    }
    return null;
  }, [fightData.takeoutType, appCtx.restaurant, appCtx.isDelivery]);

  const handleClose = () => {
    setClosing(true);
    // setTimeout(() => {
    //   onClose();
    //   setClosing(false);
    // }, 300);
  };

  const gameData = {
    makerTeamAcronym: fightData.makerTeamAcronym,
    takerTeamAcronym: fightData.takerTeamAcronym,
    makerTeamColor: fightData.makerOutcomeTeamColor,
    takerTeamColor: fightData.takerOutcomeTeamColor,
    takerName: fightData.takerName,
    makerName: fightData.makerName,
  };

  const handleGoToVenuePage = () => {
    appDispatchCtx.dispatch({
      action: ACTIONS.SET_ON_SELECT_VENUE,
      payload: venue => {
        onSelectVenue(venue);
        appDispatchCtx.dispatch({
          action: ACTIONS.SET_ON_SELECT_VENUE,
          payload: null,
        });
      },
    });
    history.push({
      pathname: '/app/restaurant',
      state: { tab: 'delivery' },
    });
    setIsPickVenuePopupOpen(false);
    onClose();
  };

  const handleAccept = () => {
    const trySkipToSummary = (takerVenueId, modalRef) => {
      if (takerVenueId === fightData.restaurantId) {
        modalRef?.current?.skipToSummary();
      } else {
        modalRef?.current?.present();
      }
    };

    // Allow different venue for delivery fights
    if (fightData.takeoutType === TAKEOUT_TYPES.DELIVERY) {
      if (appCtx.restaurant && appCtx.isDelivery) {
        trySkipToSummary(appCtx.restaurant.id, fightModalRef);
      } else {
        setOnSelectVenue(() => venue => {
          pageCtx.modals.takerProcess.setProps({
            fightData,
            optionalTakerVenue: {
              id: venue.id,
              name: venue.name,
              takeoutType: appCtx.isDelivery ? TAKEOUT_TYPES.DELIVERY : TAKEOUT_TYPES.IN_PERSON,
            },
          });
          trySkipToSummary(venue.id, pageCtx.modals.takerProcess.ref);
        });
        setIsPickVenuePopupOpen(true);
      }
    } else {
      fightModalRef?.current?.skipToSummary();
    }
    // onClose();
  };

  const handleReject = async itemId => {
    try {
      await rejectFight(bearerToken, itemId);
      onClose();
    } catch (error) {
      console.error('Error rejecting fight:', error);
    }
  };

  const handleModifyFight = () => {
    // Allow different venue for delivery fights
    if (fightData.takeoutType === TAKEOUT_TYPES.DELIVERY) {
      if (appCtx.restaurant && appCtx.isDelivery) {
        fightModalRef?.current?.present();
      } else {
        setOnSelectVenue(() => venue => {
          pageCtx.modals.takerProcess.setProps({
            fightData,
            optionalTakerVenue: {
              id: venue.id,
              name: venue.name,
              takeoutType: appCtx.isDelivery ? TAKEOUT_TYPES.DELIVERY : TAKEOUT_TYPES.IN_PERSON,
            },
          });
          pageCtx.modals.takerProcess.ref?.current?.present();
        });
        setIsPickVenuePopupOpen(true);
      }
    } else {
      fightModalRef?.current?.present();
    }
    // onClose();
  };

  function normalizeWomenTeamName(name) {
    return name
      .replace(/\bW\b/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  if (!isOpen) return null;
  return (
    <div
      className={`taker-modal-overlay ${isPlatform('ios') ? 'ios' : ''}`}
      onClick={handleClose}
      role='button'
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClose();
        }
      }}>
      <div
        className={`taker-modal ${closing ? 'closing' : ''}`}
        role='button'
        tabIndex={0}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
          }
        }}>
        <button className='close-button' type='button' onClick={() => onClose()}>
          X
        </button>
        <h3 className='challenger'>{fightData.makerName} challenged you!</h3>
        <p className='item-details'>
          {' '}
          For{' '}
          {fightData.restaurantItems.map((item, index) => (
            <span className='item-name' key={item.menu_item_id}>
              {item.quantity} x {item.name}
              {index < fightData.restaurantItems.length - 1 ? ', ' : ''}
            </span>
          ))}{' '}
          <span className='restaurant-name'>at {fightData.restaurantName}</span>
        </p>
        <div className='item-card'>
          <TakerGamePanel
            gameData={gameData}
            leftColor={fightData.makerOutcomeTeamColor}
            rightColor={fightData.takerOutcomeTeamColor}
          />
          <p className='taker-outcome'>To win {normalizeWomenTeamName(fightData.takerOutcomePhrase)}</p>
        </div>
        <IonButton className='modify-button' onClick={() => handleModifyFight()}>
          Modify
        </IonButton>
        <IonButton className='accept-button' onClick={() => handleAccept()}>
          Accept
        </IonButton>
        <button className='reject-button' type='button' onClick={() => handleReject(fightData.id)}>
          Reject
        </button>
        <StartFightContextProvider>
          <TakerFightProcess
            ref={fightModalRef}
            fightData={fightData}
            notificationClose={onClose}
            optionalTakerVenue={optionalTakerVenue}
          />
        </StartFightContextProvider>
        <TakerError
          isOpen={isPickVenuePopupOpen}
          onClose={() => setIsPickVenuePopupOpen(false)}
          setIsOpen={setIsPickVenuePopupOpen}
          msg='This fight requires you choose a delivery venue.'
          onButtonClick={handleGoToVenuePage}
          buttonText='Go to Venues page'
        />
      </div>
    </div>
  );
};

export default TakerNotification;
